import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ExternalLink } from "lucide-react";
import { useFunnel } from "../context/FunnelContext";
import { StepHeader } from "../components/StepHeader";
import { ProgressBar } from "../components/ProgressBar";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDAR_LINK = process.env.REACT_APP_CALENDAR_LINK || "#";
const TIME_WINDOWS = ["Weekday mornings", "Weekday afternoons", "Weekday evenings", "Weekend"];

export default function BookingPage() {
  const { answers, getSegment, setAnswer } = useFunnel();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", preferred_time: TIME_WINDOWS[0], timezone_pref: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.timezone_pref) {
      toast.error("Please fill in your name, email, and timezone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, quiz_answers: answers, segment: getSegment() });
      setAnswer("visitor_name", form.name);
      navigate("/thank-you");
    } catch (err) {
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      data-testid="booking-page"
      className="min-h-screen px-6 py-28 bg-[#FBF9F5]"
    >
      <ProgressBar step={5} total={6} />
      <div className="max-w-xl mx-auto">
        <StepHeader step={5} total={6} label="Book Your Call" />
        <h1 className="font-serif text-3xl sm:text-4xl text-[#1E2D24] mb-4">
          Would you like a gentle space to talk about what you're going through?
        </h1>
        <p className="text-[#5F6A62] text-base sm:text-lg leading-relaxed mb-8">
          You're invited to a free "Clarity &amp; Calm" call — a 30–45 minute conversation to explore your
          needs and see whether soul-centred coaching feels right for you. There is no obligation to
          continue.
        </p>

        <a
          href={CALENDAR_LINK}
          target="_blank"
          rel="noreferrer"
          data-testid="external-calendar-link"
          className="inline-flex items-center gap-2 mb-8 text-sm text-[#4A6B56] underline hover:text-[#C47B5E] transition-colors"
        >
          Prefer to pick a slot yourself? Open our calendar <ExternalLink className="w-4 h-4" />
        </a>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label htmlFor="name">Your name</Label>
            <Input data-testid="booking-name-input" id="name" name="name" value={form.name} onChange={handleChange} placeholder="How you'd like to be called" />
          </div>
          <div>
            <Label htmlFor="email">Email address</Label>
            <Input data-testid="booking-email-input" id="email" type="email" name="email" value={form.email} onChange={handleChange} placeholder="you@example.com" />
          </div>
          <div>
            <Label htmlFor="preferred_time">Preferred time window</Label>
            <select
              data-testid="booking-time-select"
              id="preferred_time"
              name="preferred_time"
              value={form.preferred_time}
              onChange={handleChange}
              className="w-full mt-1 px-4 py-2.5 rounded-xl border border-[#4A6B56]/20 bg-white text-[#232B25] focus:outline-none focus:ring-2 focus:ring-[#4A6B56]"
            >
              {TIME_WINDOWS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="timezone_pref">Your timezone</Label>
            <Input data-testid="booking-timezone-input" id="timezone_pref" name="timezone_pref" value={form.timezone_pref} onChange={handleChange} placeholder="e.g. IST, EST, GMT+1" />
          </div>
          <div>
            <Label htmlFor="message">Anything you'd like to share before our call? (optional)</Label>
            <Textarea data-testid="booking-message-input" id="message" name="message" value={form.message} onChange={handleChange} placeholder="You're free to say as much or as little as feels right." />
          </div>
          <p className="text-xs text-[#5F6A62]/80 italic">You are free to say yes, no, or not yet. This is your journey.</p>
          <button
            type="submit"
            disabled={submitting}
            data-testid="booking-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#4A6B56] text-[#FBF9F5] font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-60"
          >
            {submitting ? "Sending..." : "Book your free Clarity & Calm call"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
      </div>
    </motion.main>
  );
}
